import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { HelpCircle, Printer, ArrowRight, CheckCircle2, Star, Sparkles, Zap, School } from 'lucide-react';

import { FileUploadStep, PreferencesStep, PaymentStep, SuccessStep } from '@/components/customer-flow';
import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/seo/SEOHead';
import { 
  createLocalBusinessSchema, 
  createServiceSchema, 
  createFAQPageSchema, 
  createBreadcrumbListSchema, 
  createVideoObject, 
  createSpeakable 
} from '@/utils/schemaGenerator';
import { SERVICES } from '@/data/servicesData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const loadCashfreeScript = () => {
  return new Promise((resolve) => {
    if (window.Cashfree) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const CustomerFlow = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [kiosk, setKiosk] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Form data - multi-file support
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  
  const [preferences, setPreferences] = useState({
    copies: 1,
    color_mode: 'bw',
    duplex: false,
    orientation: 'portrait',
    paper_size: 'A4'
  });
  
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [jobId, setJobId] = useState('');
  const [cost, setCost] = useState(0);
  const [queueInfo, setQueueInfo] = useState(null);

  const cleanupScroll = useCallback(() => {
    document.body.style.overflow = 'auto';
    document.body.style.pointerEvents = 'auto';
    const lingeringContainers = document.querySelectorAll('.razorpay-container, .razorpay-checkout-frame');
    lingeringContainers.forEach(el => el.remove());
  }, []);

  const fetchOrCreateDemoKiosk = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/kiosks`);
      if (response.data && response.data.length > 0) {
        const sohnaStore = response.data.find(k => k.name === 'Inchpaper Sohna Store');
        const selectedKiosk = sohnaStore || response.data[0];
        setKiosk(selectedKiosk);
      } else {
        const newKiosk = await axios.post(`${API}/kiosks`, {
          name: 'Inchpaper Sohna Store',
          location: 'Sohna, Haryana',
          printer_ip: '192.168.1.100'
        });
        setKiosk(newKiosk.data);
      }
    } catch (error) {
      console.error('Error setting up kiosk:', error);
      // Fallback kiosk so customer flow is always active and usable
      setKiosk({
        id: 'kiosk-sohna-store',
        name: 'Inchpaper Sohna Store',
        location: 'Sohna, Haryana',
        printer_ip: '192.168.31.50',
        status: 'active'
      });
    }
  }, []);

  useEffect(() => {
    fetchOrCreateDemoKiosk();
  }, [fetchOrCreateDemoKiosk]);

  useEffect(() => {
    cleanupScroll();
  }, [step, cleanupScroll]);

  // Handle return from Razorpay redirect payment flow
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const returnedJobId = searchParams.get('job_id');

    if (paymentStatus === 'success' && returnedJobId) {
      setJobId(returnedJobId);
      setQueueInfo({
        queue_position: 1,
        estimated_wait_mins: 1
      });
      setStep(5);
      toast.success('Payment verified! Your print job is queued for printing.');
    } else if (paymentStatus === 'failed') {
      toast.error('Payment declined or cancelled. Please try again.');
    }
  }, [searchParams]);

  // Handle files uploaded from FileUploadStep
  const handleFilesUploaded = (files) => {
    setUploadedFiles(files);
    const pages = files.reduce((sum, f) => sum + f.pages, 0);
    setTotalPages(pages);
    setStep(2);
  };

  const handleCreateJob = async () => {
    if (!kiosk) {
      toast.error('Please wait for kiosk to load or scan a QR code');
      return;
    }
    
    if (!customerName || customerName.trim().length < 2) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!customerPhone || customerPhone.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      // Build documents array for multi-file support
      const documents = uploadedFiles.map(f => ({
        filename: f.filename,
        file_path: f.file_path,
        size: f.size,
        pages: f.pages
      }));

      // Build document name (first file or count)
      const documentName = uploadedFiles.length === 1 
        ? uploadedFiles[0].filename 
        : `${uploadedFiles.length} documents`;

      const jobData = {
        kiosk_id: kiosk.id,
        document_name: documentName,
        document_url: uploadedFiles.length === 1 ? uploadedFiles[0].file_path : null,
        documents: documents,
        preferences,
        total_pages: totalPages,
        customer_name: customerName.trim(),
        customer_phone: `+91${customerPhone}`,
        customer_email: customerEmail
      };

      const response = await axios.post(`${API}/print-jobs`, jobData);
      setJobId(response.data.id);
      setCost(response.data.cost);
      toast.success('Print job created');
      setStep(3);
    } catch (error) {
      console.error('Job creation error:', error);
      toast.error('Failed to create print job');
    } finally {
      setLoading(false);
    }
  };

  const openInNativeBrowser = () => {
    const currentUrl = window.location.href;
    const isAndroid = /Android/i.test(navigator.userAgent);
    if (isAndroid) {
      const cleanUrl = currentUrl.replace(/^https?:\/\//, '');
      window.location.href = `intent://${cleanUrl}#Intent;scheme=https;package=com.android.chrome;end`;
    } else {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(currentUrl);
        toast.success('URL copied! Open Safari or Chrome and paste to continue payment.');
      } else {
        window.open(currentUrl, '_system');
      }
    }
  };

  const handleDemoPayment = async () => {
    setLoading(true);
    cleanupScroll();
    try {
      const verifyResponse = await axios.post(`${API}/payments/verify`, {
        job_id: jobId,
        is_demo_bypass: true
      });
      if (verifyResponse.data.queue_info) {
        setQueueInfo(verifyResponse.data.queue_info);
      }
      toast.success('Demo Payment verified! Print job queued.');
      setStep(5);
    } catch (err) {
      console.error('Demo payment error:', err);
      toast.error('Failed to verify demo payment.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    cleanupScroll();

    try {
      const paymentResponse = await axios.post(`${API}/payments/create`, {
        job_id: jobId,
        amount: parseFloat(cost),
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail
      });

      const data = paymentResponse.data;

      if (!data.payment_session_id) {
        toast.error(data.error || 'Failed to create Cashfree payment session.');
        setLoading(false);
        return;
      }

      // Live Cashfree SDK Checkout
      const loaded = await loadCashfreeScript();
      if (!loaded || !window.Cashfree) {
        toast.error('Unable to load Cashfree Payment SDK');
        setLoading(false);
        return;
      }

      const cashfree = window.Cashfree({
        mode: (data.environment || 'SANDBOX').toLowerCase()
      });

      cashfree.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: '_modal'
      }).then(async (result) => {
        if (result.error) {
          console.error('Cashfree checkout error:', result.error);
          toast.error(result.error.message || 'Payment cancelled or closed.');
          setLoading(false);
        } else if (result.redirect) {
          console.log('Payment redirecting...');
        } else {
          // Verify completed payment with backend
          try {
            const verifyResponse = await axios.post(`${API}/payments/verify`, {
              job_id: jobId,
              order_id: data.order_id
            });

            if (verifyResponse.data.success) {
              if (verifyResponse.data.queue_info) {
                setQueueInfo(verifyResponse.data.queue_info);
              }
              toast.success('Payment successful! Your print job is queued.');
              setStep(5);
            } else {
              toast.error(verifyResponse.data.error || 'Payment verification failed.');
            }
          } catch (err) {
            console.error('Verify error:', err);
            toast.error(err.response?.data?.error || 'Payment verification failed.');
          } finally {
            setLoading(false);
          }
        }
      }).catch((err) => {
        console.error('Cashfree checkout error:', err);
        toast.error('Unable to open Cashfree checkout.');
        setLoading(false);
      });

    } catch (error) {
      console.error('Payment error:', error);
      const errMsg = error.response?.data?.error || error.response?.data?.message || error.response?.data?.detail || error.message || 'Payment initialization failed';
      toast.error(errMsg);
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <FileUploadStep
            onFilesUploaded={handleFilesUploaded}
            loading={loading}
            setLoading={setLoading}
          />
        );

      case 2:
        return (
          <PreferencesStep
            uploadedFiles={uploadedFiles}
            totalPages={totalPages}
            preferences={preferences}
            setPreferences={setPreferences}
            customerName={customerName}
            setCustomerName={setCustomerName}
            customerPhone={customerPhone}
            setCustomerPhone={setCustomerPhone}
            customerEmail={customerEmail}
            setCustomerEmail={setCustomerEmail}
            onBack={() => setStep(1)}
            onContinue={handleCreateJob}
            loading={loading}
          />
        );

      case 3:
        return (
          <PaymentStep
            uploadedFiles={uploadedFiles}
            totalPages={totalPages}
            preferences={preferences}
            cost={cost}
            onBack={() => setStep(2)}
            onPay={handlePayment}
            onDemoPay={handleDemoPayment}
            loading={loading}
          />
        );

      case 4:
      case 5:
        return (
          <SuccessStep
            jobId={jobId}
            queueInfo={queueInfo}
            customerEmail={customerEmail}
          />
        );

      default:
        return null;
    }
  };

  const homeFaqs = [
    { q: "How do I print documents using Inchpaper?", a: "Upload your PDF or Word document online, configure paper size (A4/A3) and color settings, complete UPI payment, and scan the QR code at any Inchpaper kiosk to print instantly." },
    { q: "What is the price of printing per page?", a: "Black & white A4 printing starts at ₹2/page, and full color A4 printing starts at ₹10/page. Discounted bulk pricing is available for large documents." },
    { q: "Where are Inchpaper kiosks located?", a: "Inchpaper self-service print kiosks are available 24x7 across university campuses, corporate parks, and retail centers in Gurgaon, Delhi NCR, and Sohna." }
  ];

  const homeSchemas = [
    createLocalBusinessSchema(),
    createServiceSchema({
      name: "Instant Self-Service Document Printing",
      description: "Upload PDF online and collect instant A4/A3 black & white or color prints at 24x7 Inchpaper kiosks.",
      url: "https://print.inchpaper.com/",
      price: "2.00"
    }),
    createFAQPageSchema(homeFaqs),
    createBreadcrumbListSchema([{ name: "Home", url: "/" }]),
    createVideoObject({
      name: "How to Print Documents in 10 Seconds at Inchpaper Kiosks",
      description: "A quick demonstration of uploading PDF online and scanning QR code at Inchpaper self-service kiosk.",
      embedUrl: "https://print.inchpaper.com/#how-it-works"
    }),
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Inchpaper Print - Online Document Printing",
      "speakable": createSpeakable(["h1", "h2", ".speakable-text"])
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between antialiased">
      <SEOHead
        title="Online Document Printing Services | Print Anytime, Anywhere | Inchpaper"
        description="India's leading self-service document printing platform. Upload PDF online, select paper options, pay via UPI, and collect instant high-quality prints from local kiosks."
        keywords="print pdf online, online xerox, document printing near me, spiral binding, color print online, inchpaper kiosk"
        canonical="https://print.inchpaper.com/"
        schemas={homeSchemas}
      />

      <Navbar />

      <main className="grow">
        <div id="upload" className="w-full max-w-6xl mx-auto p-2 sm:p-4 py-6">
          {/* Header Tagline */}
          <div className="text-center mb-4 sm:mb-8 animate-fade-in space-y-1">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#7d0909] tracking-tight">
              Printezy
            </h2>
            <h1 className="text-xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              India&apos;s Print-on-Demand Platform
            </h1>
            <p className="text-xs sm:text-sm text-slate-600 max-w-2xl mx-auto pt-1 font-medium">
              Upload, Customize, Pay Online, and Print Anytime from Inchpaper Print Kiosks Across India in 10 Seconds.
            </p>
          </div>

          {/* Step Indicator */}
          <div className="flex justify-center mb-4 sm:mb-8 px-2">
            <div className="flex items-center gap-1 sm:gap-4">
              {[
                { num: 1, label: 'Upload' },
                { num: 2, label: 'Details' },
                { num: 3, label: 'Pay' },
                { num: 5, label: 'Done' }
              ].map((s, idx, arr) => (
                <div key={s.num} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-medium text-sm sm:text-base transition-all ${
                        step >= s.num
                          ? 'bg-[#7d0909] text-white shadow-md'
                          : 'bg-slate-200 text-slate-600'
                      }`}
                      data-testid={`step-${s.num}`}
                    >
                      {idx + 1}
                    </div>
                    <span className="text-[10px] sm:text-xs text-slate-500 font-medium mt-1">{s.label}</span>
                  </div>
                  {idx < arr.length - 1 && (
                    <div
                      className={`w-4 sm:w-12 h-0.5 sm:h-1 mb-4 sm:mb-5 ${
                        step > s.num ? 'bg-[#7d0909]' : 'bg-slate-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="flex justify-center animate-fade-in px-2 mb-6">
            {renderStepContent()}
          </div>

          {/* Centered Help Button */}
          <div className="flex justify-center mb-10 animate-fade-in">
            <Link 
              to="/faq" 
              className="inline-flex items-center gap-2.5 px-5 py-2 bg-white hover:bg-slate-50 text-slate-900 font-bold text-sm sm:text-base rounded-full border border-slate-300 shadow-xs transition-all hover:border-[#7d0909]"
              data-testid="help-btn"
            >
              <div className="w-5 h-5 rounded-full border-2 border-[#7d0909] text-[#7d0909] flex items-center justify-center font-bold text-xs leading-none shrink-0">
                ?
              </div>
              <span className="text-[#1e293b] font-bold tracking-tight">Need Help</span>
            </Link>
          </div>
        </div>

        {/* Institutional Kiosk Setup Callout Card (White Background) */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50/50">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-8 sm:p-10 text-center space-y-5">
              <div className="inline-flex items-center gap-2 bg-red-50 text-[#7d0909] text-xs font-bold px-3.5 py-1.5 rounded-full border border-red-100">
                <School className="w-3.5 h-3.5" />
                <span>Campus & Corporate Printing</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight max-w-2xl mx-auto leading-tight">
                Want to setup Kiosk in your School, College, or Office?
              </h2>
              
              <p className="text-slate-600 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
                Get automated 24x7 self-service document printing, photocopies, and binding installed at your institution with zero operational hassle.
              </p>

              <div className="pt-2">
                <Link
                  to="/contact-us#kiosk-setup"
                  className="inline-flex items-center gap-2 bg-white hover:bg-red-50 text-[#7d0909] border-2 border-[#7d0909] font-extrabold text-sm px-8 py-3.5 rounded-2xl transition-all shadow-xs hover:shadow-md hover:scale-105"
                >
                  <span>Contact Us</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 25 Print Services Showcase Grid (SEO Section) */}
        <section id="all-services" className="bg-white border-t border-slate-200 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto space-y-10">
            <div className="text-center space-y-3 max-w-3xl mx-auto">
              <span className="inline-block bg-red-100 text-[#7d0909] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Comprehensive Print Solutions
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                Explore All 25 On-Demand Printing Services
              </h2>
              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                From high-volume B&W assignments to glossy color posters, hardbound thesis binding, and instant online xerox photocopies.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {SERVICES.map((svc) => (
                <div 
                  key={svc.slug}
                  className="bg-slate-50/80 p-6 rounded-2xl border border-slate-200/80 hover:border-red-300 hover:shadow-md transition-all flex flex-col justify-between group"
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{svc.category}</span>
                    </div>

                    <h3 className="font-bold text-slate-900 text-lg group-hover:text-[#7d0909] transition-colors leading-snug">
                      <Link to={`/${svc.slug}`}>
                        {svc.h1}
                      </Link>
                    </h3>

                    <p className="text-slate-600 text-xs leading-relaxed line-clamp-2">
                      {svc.tagline}
                    </p>
                  </div>

                  <div className="pt-4 mt-4 border-t border-slate-200/60 flex items-center justify-between">
                    <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-500">
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                      <span>{svc.rating}</span>
                    </div>

                    <Link 
                      to={`/${svc.slug}`}
                      className="text-xs font-bold text-[#7d0909] group-hover:underline flex items-center gap-1"
                    >
                      <span>Explore Service</span>
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CustomerFlow;
